/**
 * This file defines the connection between the web client and the trace server.
 * The intention is that the implementation is a 1-1 mapping to the trace
 * server's API. This file should not contain any business logic. If possible,
 * we could generate this from `trace_server.py`. Which in effect is a perfect
 * mapping of `weave/trace_server/trace_server_interface.py` as a web service.
 *
 * These types MUST be kept in sync with the types defined in
 * `weave/trace_server/trace_server_interface.py`. Please modify with care.
 *
 * TODO: Currently, we only implement Call Read and Call Query - there are
 * several other endpoints that we should implement.
 */

import {getCookie} from '@wandb/weave/common/util/cookie';
import {HTTPError} from '@wandb/weave/errors';
import {
  createStainlessTraceClient,
  throwAsHTTPError,
} from '@wandb/weave/stainlessTraceClient';

import {
  type AgentConversationChatReq,
  type AgentConversationChatRes,
  type AgentConversationSpansReq,
  type AgentConversationSpansRes,
  type AgentCustomAttrsSchemaReq,
  type AgentCustomAttrsSchemaRes,
  type AgentSearchReq,
  type AgentSearchRes,
  type AgentSpansQueryReq,
  type AgentSpansQueryRes,
  type AgentSpanStatsReq,
  type AgentSpanStatsRes,
  type AgentsQueryReq,
  type AgentsQueryRes,
  type AgentTraceChatReq,
  type AgentTraceChatRes,
  type AgentVersionsQueryReq,
  type AgentVersionsQueryRes,
  type AnnotationQueueAddCallsReq,
  type AnnotationQueueAddCallsRes,
  type AnnotationQueueCreateReq,
  type AnnotationQueueCreateRes,
  type AnnotationQueueDeleteReq,
  type AnnotationQueueDeleteRes,
  type AnnotationQueueItemsQueryReq,
  type AnnotationQueueItemsQueryRes,
  type AnnotationQueueReadReq,
  type AnnotationQueueReadRes,
  type AnnotationQueueSchema,
  type AnnotationQueuesQueryReq,
  type AnnotationQueuesQueryRes,
  type AnnotationQueuesStatsReq,
  type AnnotationQueuesStatsRes,
  type AnnotationQueueUpdateReq,
  type AnnotationQueueUpdateRes,
  type AnnotatorQueueItemsProgressUpdateReq,
  type AnnotatorQueueItemsProgressUpdateRes,
  type CallsScoreReq,
  type CallsScoreRes,
  type CallStatsReq,
  type CallStatsRes,
  type CallsUsageReq,
  type CallsUsageRes,
  type CompletionsCreateStreamReq,
  type CompletionsCreateStreamRes,
  ContentType,
  type CustomRuntimeApplyReq,
  type CustomRuntimeApplyRes,
  type DatasetSourcesLinkReq,
  type DatasetSourcesLinkRes,
  type DatasetSourcesQueryReq,
  type DatasetSourcesQueryRes,
  type EvalResultsQueryReq,
  type EvalResultsQueryRes,
  type EvaluateModelReq,
  type EvaluateModelRes,
  type EvaluationStatusReq,
  type EvaluationStatusRes,
  type FeedbackAggregateReq,
  type FeedbackAggregateRes,
  type FeedbackCreateReq,
  type FeedbackCreateRes,
  type FeedbackPayloadSchemaReq,
  type FeedbackPayloadSchemaRes,
  type FeedbackPurgeReq,
  type FeedbackPurgeRes,
  type FeedbackQueryReq,
  type FeedbackQueryRes,
  type FeedbackReplaceReq,
  type FeedbackReplaceRes,
  type FeedbackStatsReq,
  type FeedbackStatsRes,
  type ImageGenerationCreateReq,
  type ImageGenerationCreateRes,
  type ProjectIngestSamplingSettingsReadReq,
  type ProjectIngestSamplingSettingsReadRes,
  type ProjectStatsReq,
  type ProjectStatsRes,
  type ProjectTTLSettingsReadReq,
  type ProjectTTLSettingsReadRes,
  type ProjectTTLSettingsUpdateReq,
  type ProjectTTLSettingsUpdateRes,
  type RescoreEvaluationReq,
  type RescoreEvaluationRes,
  type SourceDatasetsQueryReq,
  type SourceDatasetsQueryRes,
  type TableCreateReq,
  type TableCreateRes,
  type TableUpdateReq,
  type TableUpdateRes,
  type ThreadSchema,
  type ThreadsQueryReq,
  type ThreadsQueryRes,
  type TraceCallReadReq,
  type TraceCallReadRes,
  type TraceCallSchema,
  type TraceCallsDeleteReq,
  type TraceCallsQueryReq,
  type TraceCallsQueryRes,
  type TraceCallsQueryStatsReq,
  type TraceCallsQueryStatsRes,
  type TraceCallUpdateReq,
  type TraceFileContentReadReq,
  type TraceFileContentReadRes,
  type TraceFilesResolveUrlReq,
  type TraceFilesResolveUrlRes,
  type TraceObjAddTagsReq,
  type TraceObjAddTagsRes,
  type TraceObjCreateReq,
  type TraceObjCreateRes,
  type TraceObjDeleteReq,
  type TraceObjDeleteRes,
  type TraceObjQueryReq,
  type TraceObjQueryRes,
  type TraceObjReadReq,
  type TraceObjReadRes,
  type TraceObjRemoveAliasesReq,
  type TraceObjRemoveAliasesRes,
  type TraceObjRemoveTagsReq,
  type TraceObjRemoveTagsRes,
  type TraceObjSetAliasesReq,
  type TraceObjSetAliasesRes,
  type TraceRefsReadBatchReq,
  type TraceRefsReadBatchRes,
  type TraceTableQueryReq,
  type TraceTableQueryRes,
  type TraceTableQueryStatsBatchReq,
  type TraceTableQueryStatsBatchRes,
  type TraceUsageReq,
  type TraceUsageRes,
} from './traceServerClientTypes';

// Maximum size in bytes for a single stream request (500 MB)
// This prevents browser crashes from excessively large payloads
const MAX_STREAM_SIZE_BYTES = 500 * 1024 * 1024;

// /calls/stream_query withholds its response headers until a first chunk of
// output exists, and the formatter only emits one past roughly eight million
// characters, so for a normal response the headers arrive once the whole query
// has already run. The client stops its timer when the headers arrive, so the
// 5-minute default would be capping the query itself.
const CALLS_STREAM_TIMEOUT_MS = 24 * 60 * 60 * 1000;

export class DirectTraceServerClient {
  private baseUrl: string;
  private readonly stainlessClient: ReturnType<
    typeof createStainlessTraceClient
  >;
  private inFlightFetchesRequests: Record<
    string,
    Record<
      string,
      Array<{
        resolve: (res: any) => void;
        reject: (err: any) => void;
      }>
    >
  > = {};

  constructor(baseUrl: string) {
    this.inFlightFetchesRequests = {};
    this.baseUrl = baseUrl;
    this.stainlessClient = createStainlessTraceClient(baseUrl);
  }

  public callsDelete(req: TraceCallsDeleteReq): Promise<void> {
    // Signature is unchanged Promise<void>; drop the body rather than widen it.
    return this.stainlessRequest('/calls/delete', req, () =>
      this.stainlessClient.calls.delete(req).then(() => undefined)
    );
  }

  public callUpdate(req: TraceCallUpdateReq): Promise<void> {
    // Signature is unchanged Promise<void>; drop the body rather than widen it.
    return this.stainlessRequest('/call/update', req, () =>
      this.stainlessClient.calls.update(req).then(() => undefined)
    );
  }

  public callsQuery(req: TraceCallsQueryReq): Promise<TraceCallsQueryRes> {
    return this.makeRequest<TraceCallsQueryReq, TraceCallsQueryRes>(
      '/calls/query',
      req
    );
  }

  public callsQueryStats(
    req: TraceCallsQueryStatsReq
  ): Promise<TraceCallsQueryStatsRes> {
    return this.stainlessRequest('/calls/query_stats', req, () =>
      this.stainlessClient.calls.queryStats(req)
    );
  }

  public evalResultsQuery(req: {
    entity: string;
    project: string;
    body: EvalResultsQueryReq;
  }): Promise<EvalResultsQueryRes> {
    // The server gives `EvalResultsRow.evaluations` a default, so the field is
    // not required in the schema and the generated row type marks it optional.
    // The app type requires it, so the response is not assignable as it stands.
    // WB-39572 tracks removing the cast.
    return this.stainlessRequest(
      `/v2/${req.entity}/${req.project}/eval_results/query`,
      req.body,
      () =>
        this.stainlessClient.v2EvalResults.query(req.project, {
          entity: req.entity,
          ...req.body,
        })
    ) as Promise<EvalResultsQueryRes>;
  }

  /**
   * Compute per-call usage for a trace, with descendant rollup.
   * Returns usage metrics for each call in the trace, where each
   * call's metrics include the sum of its own usage plus all descendants' usage.
   * Use this for trace view where you want to see rolled-up metrics per call.
   */
  public traceUsage(req: TraceUsageReq): Promise<TraceUsageRes> {
    return this.stainlessRequest('/trace/usage', req, () =>
      this.stainlessClient.trace.usage(req)
    );
  }

  /**
   * Compute aggregated usage for multiple root calls, with descendant rollup.
   * Returns usage metrics for each requested root call, where each
   * root's metrics include the sum of its own usage plus all descendants' usage.
   * Use this for trace table where you want to see rolled-up metrics per root call.
   */
  public callsUsage(req: CallsUsageReq): Promise<CallsUsageRes> {
    return this.stainlessRequest('/calls/usage', req, () =>
      this.stainlessClient.calls.usage(req)
    );
  }

  public imageCreate(
    req: ImageGenerationCreateReq
  ): Promise<ImageGenerationCreateRes> {
    try {
      // The server declares `weave_call_id` nullable and the app type does not
      // allow null. `makeRequest` hid that because `response.json()` is `any`.
      // Marking the field required does not help here, because the generated
      // type keeps the null. WB-39572 tracks removing the cast, and this one
      // needs the server to drop the nullability or the app type to accept it.
      return this.stainlessRequest('/image/create', req, () =>
        this.stainlessClient.images.create(req)
      ) as Promise<ImageGenerationCreateRes>;
    } catch (error: any) {
      if (error?.api_key_name) {
        console.error('Missing LLM API key:', error.api_key_name);
      }
      return Promise.reject(error);
    }
  }

  public async callsStreamQuery(
    req: TraceCallsQueryReq,
    signal?: AbortSignal
  ): Promise<TraceCallsQueryRes> {
    // processCallsStream goes on reading the body: it caps the stream at 500 MB,
    // resumes a stream that was cut mid-line and skips a line it cannot parse,
    // none of which the generated JSONL decoder does, so asResponse() hands it
    // the raw body and it stays as it was.
    try {
      const response = await this.stainlessClient.calls
        .streamQuery(req, {signal, timeout: CALLS_STREAM_TIMEOUT_MS})
        .asResponse();

      if (!response.body) {
        return {calls: []};
      }

      return await this.processCallsStream(response.body, req, signal);
    } catch (err: any) {
      // Ignore aborts - these are expected when requests are cancelled. The
      // generated client reports one as APIUserAbortError, whose name stays
      // 'Error', so the signal is what we can check.
      if (signal?.aborted || err.name === 'AbortError') {
        return {calls: []};
      }
      console.error('calls stream query error', err);
      throwAsHTTPError(err);
    }
  }

  public async callsStreamDownload(
    req: TraceCallsQueryReq,
    contentType: ContentType = ContentType.any
  ): Promise<Blob> {
    // The generated method takes Accept as a header parameter.
    // TODO: support streaming data into a memory buffer, this .blob() method
    // is incomplete, add paging/stream construction of this blob or string. More info here:
    // https://stackoverflow.com/questions/28307789/is-there-any-limitation-on-javascript-max-blob-size
    try {
      const response = await this.stainlessClient.calls
        .streamQuery(
          {...req, accept: contentType},
          {timeout: CALLS_STREAM_TIMEOUT_MS}
        )
        .asResponse();
      return await response.blob();
    } catch (err) {
      throwAsHTTPError(err);
    }
  }

  public callRead(req: TraceCallReadReq): Promise<TraceCallReadRes> {
    // Wire shape, no mapper.
    return this.stainlessRequest('/call/read', req, () =>
      this.stainlessClient.calls.read(req)
    );
  }

  public objsQuery(req: TraceObjQueryReq): Promise<TraceObjQueryRes> {
    return this.stainlessRequest('/objs/query', req, () =>
      this.stainlessClient.objects.query(req)
    );
  }

  public objRead(req: TraceObjReadReq): Promise<TraceObjReadRes> {
    return this.stainlessRequest('/obj/read', req, () =>
      this.stainlessClient.objects.read(req)
    );
  }

  public objDelete(req: TraceObjDeleteReq): Promise<TraceObjDeleteRes> {
    return this.stainlessRequest('/obj/delete', req, () =>
      this.stainlessClient.objects.delete(req)
    );
  }

  public customRuntimeApply(
    req: CustomRuntimeApplyReq
  ): Promise<CustomRuntimeApplyRes> {
    const {entity, project, runtime_name, body} = req;
    // The server gives `max_tokens` a default, so the field is not required in
    // the schema and the generated runtime id marks it optional. The app type
    // requires it, so the response is not assignable as it stands. WB-39572
    // tracks removing the cast.
    return this.stainlessRequest(
      `/v2/${entity}/${project}/runtimes/${encodeURIComponent(runtime_name)}`,
      body,
      () =>
        this.stainlessClient.v2Runtimes.apply(runtime_name, {
          entity,
          project,
          ...body,
        })
    ) as Promise<CustomRuntimeApplyRes>;
  }

  public objAddTags(req: TraceObjAddTagsReq): Promise<TraceObjAddTagsRes> {
    const {object_id, digest, ...body} = req;
    return this.stainlessRequest(
      `/objs/${object_id}/versions/${digest}/tags`,
      req,
      () => this.stainlessClient.objects.tags.add(digest, {object_id, ...body})
    );
  }

  public objRemoveTags(
    req: TraceObjRemoveTagsReq
  ): Promise<TraceObjRemoveTagsRes> {
    const {object_id, digest, ...body} = req;
    return this.stainlessRequest(
      `/objs/${object_id}/versions/${digest}/tags/remove`,
      req,
      () =>
        this.stainlessClient.objects.tags.remove(digest, {object_id, ...body})
    );
  }

  public objSetAliases(
    req: TraceObjSetAliasesReq
  ): Promise<TraceObjSetAliasesRes> {
    const {object_id, ...body} = req;
    return this.stainlessRequest(`/objs/${object_id}/aliases`, req, () =>
      this.stainlessClient.objects.aliases.set(object_id, body)
    );
  }

  public objRemoveAliases(
    req: TraceObjRemoveAliasesReq
  ): Promise<TraceObjRemoveAliasesRes> {
    const {object_id, ...body} = req;
    return this.stainlessRequest(`/objs/${object_id}/aliases/remove`, req, () =>
      this.stainlessClient.objects.aliases.remove(object_id, body)
    );
  }

  public readBatch(req: TraceRefsReadBatchReq): Promise<TraceRefsReadBatchRes> {
    return this.stainlessRequest('/refs/read_batch', req, () =>
      this.stainlessClient.refs.readBatch(req)
    );
  }

  public objCreate(req: TraceObjCreateReq): Promise<TraceObjCreateRes> {
    const initialObjectId = req.obj.object_id;
    const sanitizedObjectId = sanitizeObjectId(initialObjectId);
    if (sanitizedObjectId !== initialObjectId) {
      // Caller is expected to sanitize the object id. We should be doing this
      // on the server, but it is currently disabled.
      throw new Error(
        `Invalid object name: ${initialObjectId}, sanitized to ${sanitizedObjectId}`
      );
    }
    return this.stainlessRequest('/obj/create', req, () =>
      this.stainlessClient.objects.create(req)
    );
  }

  public tableUpdate(req: TableUpdateReq): Promise<TableUpdateRes> {
    return this.stainlessRequest('/table/update', req, () =>
      this.stainlessClient.tables.update(req)
    ) as Promise<TableUpdateRes>;
  }

  public tableCreate(req: TableCreateReq): Promise<TableCreateRes> {
    return this.stainlessRequest('/table/create', req, () =>
      this.stainlessClient.tables.create(req)
    ) as Promise<TableCreateRes>;
  }

  public tableQuery(req: TraceTableQueryReq): Promise<TraceTableQueryRes> {
    return this.stainlessRequest('/table/query', req, () =>
      this.stainlessClient.tables.query(req)
    );
  }

  public tableQueryStatsBatch(
    req: TraceTableQueryStatsBatchReq
  ): Promise<TraceTableQueryStatsBatchRes> {
    return this.stainlessRequest('/table/query_stats_batch', req, () =>
      this.stainlessClient.tables.queryStatsBatch(req)
    );
  }

  public feedbackCreate(req: FeedbackCreateReq): Promise<FeedbackCreateRes> {
    return this.stainlessRequest('/feedback/create', req, () =>
      this.stainlessClient.feedback.create(req)
    );
  }

  // Query and purge are cast because the generated types are wider than ours:
  // free-form objects for query rows, because a caller can pick the fields,
  // and `unknown` for purge. Create and replace already match, so they need
  // no cast.
  public feedbackQuery(req: FeedbackQueryReq): Promise<FeedbackQueryRes> {
    return this.stainlessRequest('/feedback/query', req, () =>
      this.stainlessClient.feedback.query(req)
    ) as Promise<FeedbackQueryRes>;
  }

  public feedbackPurge(req: FeedbackPurgeReq): Promise<FeedbackPurgeRes> {
    return this.stainlessRequest('/feedback/purge', req, () =>
      this.stainlessClient.feedback.purge(req)
    ) as Promise<FeedbackPurgeRes>;
  }

  public feedbackReplace(req: FeedbackReplaceReq): Promise<FeedbackReplaceRes> {
    return this.stainlessRequest('/feedback/replace', req, () =>
      this.stainlessClient.feedback.replace(req)
    );
  }

  // Feedback stats is cast because the generated response makes the bucket
  // array optional and types each bucket as a free-form object, so it cannot
  // describe our shape. WB-39513 tracks removing the cast.
  public feedbackStats(req: FeedbackStatsReq): Promise<FeedbackStatsRes> {
    return this.stainlessRequest('/feedback/stats', req, () =>
      this.stainlessClient.feedback.stats(req)
    ) as Promise<FeedbackStatsRes>;
  }

  public feedbackAggregate(
    req: FeedbackAggregateReq
  ): Promise<FeedbackAggregateRes> {
    return this.makeRequest<FeedbackAggregateReq, FeedbackAggregateRes>(
      '/feedback/aggregate',
      req
    );
  }

  public feedbackPayloadSchema(
    req: FeedbackPayloadSchemaReq
  ): Promise<FeedbackPayloadSchemaRes> {
    return this.stainlessRequest('/feedback/payload_schema', req, () =>
      this.stainlessClient.feedback.payloadSchema(req)
    );
  }

  public fileContent(
    req: TraceFileContentReadReq
  ): Promise<TraceFileContentReadRes> {
    // The response is file bytes and carries no Content-Type, so the default
    // parser would fall through to `response.text()` and mangle them.
    return this.stainlessRequest('/file/content', req, async () => {
      const response = await this.stainlessClient.files
        .content(req)
        .asResponse();
      return {content: await response.arrayBuffer()};
    });
  }

  // Signs reference-bucket URIs into short-lived download URLs via Gorilla.
  // URLs are short-lived; callers refresh before expiry.
  public filesResolveUrl(
    req: TraceFilesResolveUrlReq
  ): Promise<TraceFilesResolveUrlRes> {
    // Generated `expires_at` is optional and nullable; the app type requires
    // a string. WB-39695 tracks removing the cast.
    return this.stainlessRequest('/files/resolve_url', req, () =>
      this.stainlessClient.files.resolveURL(req)
    ) as Promise<TraceFilesResolveUrlRes>;
  }

  public async completionsCreateStream(
    req: CompletionsCreateStreamReq,
    onChunk?: (chunk: any) => void,
    signal?: AbortSignal
  ): Promise<CompletionsCreateStreamRes> {
    // processCompletionStream splits _meta out, calls onChunk per token, and
    // skips a line it cannot parse, none of which the generated JSONL decoder
    // does, so asResponse() hands it the raw body and it stays as it was.
    try {
      const response = await this.stainlessClient.completions
        .createStream(req, {signal})
        .asResponse();

      if (!response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await this.processCompletionStream(response.body, onChunk);
    } catch (err: any) {
      throwAsHTTPError(err);
    }
  }

  /**
   * Process a ReadableStream of JSON lines from a calls stream.
   * Each line should be a complete TraceCallSchema JSON object.
   * Handles early termination by resuming from the last successfully parsed line.
   * @param stream The ReadableStream to process
   * @param req The original request (used for resuming on early termination)
   * @returns Promise resolving to the calls array
   */
  private processCallsStream(
    stream: ReadableStream,
    req: TraceCallsQueryReq,
    signal?: AbortSignal
  ): Promise<TraceCallsQueryRes> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const calls: TraceCallSchema[] = [];
    let lineIndex = 0;
    let totalBytesRead = 0;

    // Process a single line of JSON
    const processLine = (line: string) => {
      if (line.trim() === '') {
        return;
      }
      try {
        calls.push(JSON.parse(line));
        lineIndex++;
      } catch (err) {
        console.error(`Error parsing line ${lineIndex}:`, line, err);
        // No need to throw here, just skip the bad line.
        // throw new Error(
        //   `Failed to parse call data at line ${lineIndex}: ${err}`
        // );
      }
    };

    // Read and process the stream
    const read = (): Promise<TraceCallsQueryRes> => {
      return reader.read().then(({done, value}) => {
        if (done) {
          // Process any remaining data in the buffer
          if (buffer.trim() !== '') {
            // This is a very special case where the last line is not a
            // complete json object. This can happen if the stream is
            // terminated early. Instead of just failing, we can make a
            // new request to the server to resume the stream from the
            // last line. This should only occur in projects with massive
            // trace data (> 150MB per my own testing)
            if (lineIndex > 0) {
              const newReq = {...req};
              const origOffset = req.offset || 0;
              newReq.offset = origOffset + lineIndex;
              console.debug(
                `Early stream termination, performing a new request resuming from ${newReq.offset}`
              );
              return this.callsStreamQuery(newReq, signal).then(innerRes => {
                calls.push(...innerRes.calls);
                return {calls};
              });
            }
          }
          return {calls};
        }

        // Decode the new chunk and add it to our buffer
        buffer += decoder.decode(value, {stream: true});

        // Process complete lines from the buffer
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          processLine(line);
        }

        // Check if we've exceeded the size limit
        totalBytesRead += value.byteLength;
        if (totalBytesRead > MAX_STREAM_SIZE_BYTES) {
          // Cancel the reader to stop receiving data
          reader.cancel('Size limit exceeded').catch(err => {
            console.warn('Error cancelling stream reader:', err);
          });
          console.warn(
            `Stream size limit exceeded (${(totalBytesRead / (1024 * 1024)).toFixed(2)} MB). ` +
              `Stopping stream processing. Returning ${calls.length} calls.` +
              `Some results may be missing.`
          );
          return {calls};
        }

        // Continue reading
        return read();
      });
    };

    return read();
  }

  /**
   * Process a ReadableStream of JSON lines from a completion stream, handling both regular chunks and metadata.
   * @param stream The ReadableStream to process
   * @param onChunk Optional callback for each chunk as it arrives
   * @returns Promise resolving to the final chunks and weave_call_id
   */
  private processCompletionStream(
    stream: ReadableStream,
    onChunk?: (chunk: any) => void
  ): Promise<CompletionsCreateStreamRes> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const chunks: any[] = [];
    let weaveCallId: string | undefined;
    let spanId: string | undefined;
    let traceId: string | undefined;
    let conversationId: string | undefined;

    // Process a single line of JSON
    const processLine = (line: string) => {
      if (line.trim() === '') {
        return;
      }
      try {
        const parsed = JSON.parse(line);
        if (parsed._meta) {
          weaveCallId = parsed._meta.weave_call_id;
          spanId = parsed._meta.span_id ?? parsed._meta.weave_call_id;
          traceId = parsed._meta.trace_id;
          conversationId = parsed._meta.conversation_id;
        } else {
          chunks.push(parsed);
          onChunk?.(parsed);
        }
      } catch (err) {
        console.error('Error parsing completion chunk line:', line, err);
      }
    };

    // Read and process the stream
    const read = (): Promise<void> => {
      return reader.read().then(({done, value}) => {
        if (done) {
          // Process any remaining data in the buffer
          if (buffer.trim() !== '') {
            processLine(buffer);
          }
          return;
        }

        // Decode the new chunk and add it to our buffer
        buffer += decoder.decode(value, {stream: true});

        // Process complete lines from the buffer
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          processLine(line);
        }

        // Continue reading
        return read();
      });
    };

    return read().then(() => ({
      chunks,
      weave_call_id: weaveCallId,
      span_id: spanId,
      trace_id: traceId,
      conversation_id: conversationId,
    }));
  }

  public projectIngestSamplingSettingsRead(
    req: ProjectIngestSamplingSettingsReadReq
  ): Promise<ProjectIngestSamplingSettingsReadRes> {
    return this.stainlessRequest(
      '/project/ingest_sampling_settings/read',
      req,
      () => this.stainlessClient.projects.ingestSamplingSettings.read(req)
    );
  }

  public projectStats(req: ProjectStatsReq): Promise<ProjectStatsRes> {
    return this.stainlessRequest('/project/stats', req, () =>
      this.stainlessClient.projects.stats(req)
    );
  }

  public projectTTLSettingsRead(
    req: ProjectTTLSettingsReadReq
  ): Promise<ProjectTTLSettingsReadRes> {
    // Generated `retention_days` is optional; the app type requires
    // `number | null`. WB-39695 tracks removing the cast.
    return this.stainlessRequest('/project/ttl_settings/read', req, () =>
      this.stainlessClient.projects.ttlSettings.read(req)
    ) as Promise<ProjectTTLSettingsReadRes>;
  }

  public projectTTLSettingsUpdate(
    req: ProjectTTLSettingsUpdateReq
  ): Promise<ProjectTTLSettingsUpdateRes> {
    return this.stainlessRequest('/project/ttl_settings/update', req, () =>
      this.stainlessClient.projects.ttlSettings.update(req)
    );
  }

  public threadsStreamQuery(req: ThreadsQueryReq): Promise<ThreadsQueryRes> {
    // The parser below resumes the stream from the last complete line and skips
    // a line it cannot parse. The generated JSONL decoder does neither, so
    // asResponse() hands the body over and the parser stays as it was.
    return this.stainlessRequest('/threads/stream_query', req, async () => {
      const response = await this.stainlessClient.threads
        .streamQuery(req)
        .asResponse();
      let content = await response.text();

      // `content` is jsonl string, we need to parse it.
      if (!content) {
        return {threads: []};
      }

      if (content.endsWith('\n')) {
        content = content.slice(0, -1);
      }

      if (content === '') {
        return {threads: []};
      }

      const threads: ThreadSchema[] = [];
      const lines = content.split('\n');

      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex];
        try {
          threads.push(JSON.parse(line));
        } catch (err) {
          if (lineIndex === lines.length - 1 && lineIndex > 0) {
            // This is a very special case where the last line is not a
            // complete json object. This can happen if the stream is
            // terminated early. Instead of just failing, we can make a
            // new request to the server to resume the stream from the
            // last line. This should only occur projects with massive
            // thread data.
            const newReq = {...req};
            const origOffset = req.offset || 0;
            newReq.offset = origOffset + lineIndex;
            console.debug(
              `Early stream termination, performing a new request resuming from ${newReq.offset}`
            );

            const innerRes = await this.threadsStreamQuery(newReq);
            threads.push(...innerRes.threads);
            return {threads};
          } else {
            console.error(
              `Error parsing line ${lineIndex} of ${lines.length}: ${line}`
            );
          }
        }
      }

      return {threads};
    });
  }

  // Dataset Sources API
  public datasetSourcesQuery(
    req: DatasetSourcesQueryReq
  ): Promise<DatasetSourcesQueryRes> {
    // Generated Link marks `added_by`, `deleted_at`, and `link_metadata`
    // optional; the app type requires them. WB-39695 tracks removing the cast.
    return this.stainlessRequest('/dataset_sources/query', req, () =>
      this.stainlessClient.datasetSources.query(req)
    ) as Promise<DatasetSourcesQueryRes>;
  }

  public datasetSourcesLink(
    req: DatasetSourcesLinkReq
  ): Promise<DatasetSourcesLinkRes> {
    // Generated `created` is optional; the app type requires `boolean | null`.
    // WB-39695 tracks removing the cast.
    return this.stainlessRequest('/dataset_sources/link', req, () =>
      this.stainlessClient.datasetSources.link(req)
    ) as Promise<DatasetSourcesLinkRes>;
  }

  // Reverse query: source -> datasets.
  public sourceDatasetsQuery(
    req: SourceDatasetsQueryReq
  ): Promise<SourceDatasetsQueryRes> {
    return this.stainlessRequest(
      '/dataset_sources/source_datasets_query',
      req,
      () => this.stainlessClient.datasetSources.sourceDatasetsQuery(req)
    );
  }

  // Annotation Queue API
  public annotationQueueCreate(
    req: AnnotationQueueCreateReq
  ): Promise<AnnotationQueueCreateRes> {
    return this.stainlessRequest('/annotation_queues', req, () =>
      this.stainlessClient.annotationQueues.create(req)
    );
  }

  public annotationQueuesStreamQuery(
    req: AnnotationQueuesQueryReq
  ): Promise<AnnotationQueuesQueryRes> {
    return this.stainlessRequest('/annotation_queues/query', req, async () => {
      const stream = await this.stainlessClient.annotationQueues.query(req);
      const queues: AnnotationQueueSchema[] = [];
      try {
        for await (const queue of stream) {
          queues.push(queue);
        }
      } catch (err) {
        // A truncated stream leaves a partial trailing line and the decoder
        // rejects on it. Keep the queues decoded so far and log, the way the
        // handwritten reader did: these lists are small and a short list beats
        // an error. Only a parse failure is tolerated. A transport failure
        // mid-stream has to stay an error, because the page shows a retry for
        // it and would otherwise render a truncated list as a success.
        //
        // One narrowing remains: the handwritten reader skipped a malformed
        // line and kept the lines after it, while the decoder stops at the
        // first one. The route writes one `json.dumps` per queue, so a
        // malformed line can only be the truncated last one.
        if (!(err instanceof SyntaxError)) {
          throw err;
        }
        console.error(`Error parsing annotation queue stream: ${err}`);
      }
      return {queues};
    });
  }

  // Read and delete build the same endpoint string and the same request JSON,
  // and the coalescing key does not carry the verb, so a concurrent pair
  // collapses into whichever call registered first and the other verb never
  // reaches the network. This predates the move onto the generated client and
  // is unchanged by it. WB-39573 tracks separating the two keys.
  public annotationQueueRead(
    req: AnnotationQueueReadReq
  ): Promise<AnnotationQueueReadRes> {
    const {queue_id, project_id} = req;
    return this.stainlessRequest(`/annotation_queues/${queue_id}`, req, () =>
      this.stainlessClient.annotationQueues.read(queue_id, {project_id})
    );
  }

  // Shares a coalescing key with annotationQueueRead above. WB-39573.
  public annotationQueueDelete(
    req: AnnotationQueueDeleteReq
  ): Promise<AnnotationQueueDeleteRes> {
    const {queue_id, project_id} = req;
    return this.stainlessRequest(`/annotation_queues/${queue_id}`, req, () =>
      this.stainlessClient.annotationQueues.delete(queue_id, {project_id})
    );
  }

  public annotationQueueUpdate(
    req: AnnotationQueueUpdateReq
  ): Promise<AnnotationQueueUpdateRes> {
    const {queue_id, ...body} = req;
    return this.stainlessRequest(`/annotation_queues/${queue_id}`, body, () =>
      this.stainlessClient.annotationQueues.update(queue_id, body)
    );
  }

  public annotationQueueAddCalls(
    req: AnnotationQueueAddCallsReq
  ): Promise<AnnotationQueueAddCallsRes> {
    const {queue_id, ...body} = req;
    return this.stainlessRequest(
      `/annotation_queues/${queue_id}/items`,
      body,
      () => this.stainlessClient.annotationQueues.items.add(queue_id, body)
    );
  }

  public annotationQueueItemsQuery(
    req: AnnotationQueueItemsQueryReq
  ): Promise<AnnotationQueueItemsQueryRes> {
    const {queue_id, ...body} = req;
    return this.stainlessRequest(
      `/annotation_queues/${queue_id}/items/query`,
      body,
      () => this.stainlessClient.annotationQueues.items.query(queue_id, body)
    );
  }

  public annotationQueuesStats(
    req: AnnotationQueuesStatsReq
  ): Promise<AnnotationQueuesStatsRes> {
    return this.stainlessRequest('/annotation_queues/stats', req, () =>
      this.stainlessClient.annotationQueues.stats(req)
    );
  }

  public annotatorQueueItemsProgressUpdate(
    req: AnnotatorQueueItemsProgressUpdateReq
  ): Promise<AnnotatorQueueItemsProgressUpdateRes> {
    const {queue_id, item_id, ...body} = req;
    return this.stainlessRequest(
      `/annotation_queues/${queue_id}/items/${item_id}/progress`,
      body,
      () =>
        this.stainlessClient.annotationQueues.items.updateProgress(item_id, {
          queue_id,
          ...body,
        })
    );
  }

  public evaluateModel(req: EvaluateModelReq): Promise<EvaluateModelRes> {
    return this.stainlessRequest('/evaluations/evaluate_model', req, () =>
      this.stainlessClient.evaluations.evaluateModel(req)
    );
  }

  public rescoreEvaluation(
    req: RescoreEvaluationReq
  ): Promise<RescoreEvaluationRes> {
    return this.stainlessRequest('/evaluations/rescore', req, () =>
      this.stainlessClient.evaluations.rescore(req)
    );
  }

  public evaluationStatus(
    req: EvaluationStatusReq
  ): Promise<EvaluationStatusRes> {
    // Every status variant declares its `code` with a default, so the generated
    // members mark the discriminant optional and the union stops discriminating.
    // WB-39572 tracks removing the cast.
    return this.stainlessRequest('/evaluations/status', req, () =>
      this.stainlessClient.evaluations.status(req)
    ) as Promise<EvaluationStatusRes>;
  }

  public callsScore(req: CallsScoreReq): Promise<CallsScoreRes> {
    // `/calls/score` answers with a model that declares no fields, so the
    // generated method returns `unknown`. WB-39572 tracks removing the cast.
    // This one goes only once the response model has a field.
    return this.stainlessRequest('/calls/score', req, () =>
      this.stainlessClient.calls.score(req)
    ) as Promise<CallsScoreRes>;
  }

  // Call stats is cast because the generated response makes both bucket
  // arrays optional and types their elements as free-form objects. WB-39513
  // tracks removing the cast.
  public callStats(req: CallStatsReq): Promise<CallStatsRes> {
    return this.stainlessRequest('/calls/stats', req, () =>
      this.stainlessClient.calls.stats(req)
    ) as Promise<CallStatsRes>;
  }

  public agentsQuery(req: AgentsQueryReq): Promise<AgentsQueryRes> {
    return this.stainlessRequest('/agents/query', req, () =>
      this.stainlessClient.agents.query(req)
    );
  }

  public agentSpansQuery(req: AgentSpansQueryReq): Promise<AgentSpansQueryRes> {
    return this.stainlessRequest('/agents/spans/query', req, () =>
      this.stainlessClient.agents.spans.query(req)
    );
  }

  public agentSpansStats(req: AgentSpanStatsReq): Promise<AgentSpanStatsRes> {
    return this.stainlessRequest('/agents/spans/stats', req, () =>
      this.stainlessClient.agents.spans.stats(req)
    );
  }

  public agentCustomAttrsSchema(
    req: AgentCustomAttrsSchemaReq
  ): Promise<AgentCustomAttrsSchemaRes> {
    return this.stainlessRequest('/agents/spans/custom-attrs/schema', req, () =>
      this.stainlessClient.agents.spans.customAttrsSchema(req)
    );
  }

  public agentVersionsQuery(
    req: AgentVersionsQueryReq
  ): Promise<AgentVersionsQueryRes> {
    return this.stainlessRequest('/agents/agent-versions/query', req, () =>
      this.stainlessClient.agents.agentVersions.query(req)
    );
  }

  public agentTracesChat(req: AgentTraceChatReq): Promise<AgentTraceChatRes> {
    return this.stainlessRequest('/agents/traces/chat', req, () =>
      this.stainlessClient.agents.traces.chat(req)
    ) as Promise<AgentTraceChatRes>;
  }

  public agentConversationChat(
    req: AgentConversationChatReq
  ): Promise<AgentConversationChatRes> {
    return this.stainlessRequest('/agents/conversations/chat', req, () =>
      this.stainlessClient.agents.conversations.chat(req)
    ) as Promise<AgentConversationChatRes>;
  }

  public agentConversationSpans(
    req: AgentConversationSpansReq
  ): Promise<AgentConversationSpansRes> {
    return this.stainlessRequest('/agents/conversations/spans', req, () =>
      this.stainlessClient.agents.conversations.spans(req)
    );
  }

  public agentSearch(req: AgentSearchReq): Promise<AgentSearchRes> {
    return this.stainlessRequest('/agents/search', req, () =>
      this.stainlessClient.agents.search(req)
    ) as Promise<AgentSearchRes>;
  }

  // Same in-flight coalescing as makeRequest: one network call per
  // (endpoint, JSON body) while a request is already outstanding.
  private stainlessRequest<T>(
    endpoint: string,
    req: unknown,
    run: () => Promise<T>
  ): Promise<T> {
    // A request that cannot be serialized has no coalescing key, so there is
    // nothing to register and nothing to clean up. Reject instead of throwing:
    // `makeRequest` is async, so callers of the methods that already moved
    // onto the generated client expect a promise, and several of them chain
    // with `then` rather than `await`.
    let reqBody: string;
    try {
      reqBody = JSON.stringify(req);
    } catch (err) {
      return Promise.reject(err);
    }
    if (!this.inFlightFetchesRequests[endpoint]) {
      this.inFlightFetchesRequests[endpoint] = {};
    }
    let needsFetch = false;
    if (!this.inFlightFetchesRequests[endpoint][reqBody]) {
      this.inFlightFetchesRequests[endpoint][reqBody] = [];
      needsFetch = true;
    }
    const prom = new Promise<T>((resolve, reject) => {
      this.inFlightFetchesRequests[endpoint][reqBody].push({resolve, reject});
    });
    if (!needsFetch) {
      return prom;
    }
    // Generated methods that take a path parameter build the URL synchronously
    // and throw on a segment they cannot safely put in a path. Turning that
    // into a rejection sends it to the catch below, which is what clears the
    // in-flight entry; otherwise the next identical call never settles.
    let started: Promise<T>;
    try {
      started = run();
    } catch (err) {
      started = Promise.reject(err);
    }
    started
      .then(res => {
        const waiters = [
          ...(this.inFlightFetchesRequests[endpoint]?.[reqBody] ?? []),
        ];
        delete this.inFlightFetchesRequests[endpoint][reqBody];
        waiters.forEach(({resolve}) => {
          resolve(res);
        });
      })
      .catch(err => {
        let mapped: unknown = err;
        try {
          throwAsHTTPError(err);
        } catch (e) {
          mapped = e;
        }
        const waiters = [
          ...(this.inFlightFetchesRequests[endpoint]?.[reqBody] ?? []),
        ];
        delete this.inFlightFetchesRequests[endpoint][reqBody];
        waiters.forEach(({reject}) => {
          reject(mapped);
        });
      });
    return prom;
  }

  private makeRequest = async <QT, ST>(
    endpoint: string,
    req: QT,
    responseReturnType: 'json' | 'text' | 'arrayBuffer' = 'json',
    contentType?: ContentType,
    options?: {
      method?: 'GET' | 'POST' | 'DELETE' | 'PUT';
      queryParams?: Record<string, string>;
    }
  ): Promise<ST> => {
    const method = options?.method ?? 'POST';
    let url = `${this.baseUrl}${endpoint}`;

    // Add query parameters for GET requests
    if (['GET', 'DELETE'].includes(method) && options?.queryParams) {
      const params = new URLSearchParams(options.queryParams);
      url = `${url}?${params.toString()}`;
    }

    const reqBody = JSON.stringify(req);
    let needsFetch = false;
    if (!this.inFlightFetchesRequests[endpoint]) {
      this.inFlightFetchesRequests[endpoint] = {};
    }
    if (!this.inFlightFetchesRequests[endpoint][reqBody]) {
      this.inFlightFetchesRequests[endpoint][reqBody] = [];
      needsFetch = true;
    }

    const prom = new Promise<ST>((resolve, reject) => {
      this.inFlightFetchesRequests[endpoint][reqBody].push({resolve, reject});
    });

    if (!needsFetch) {
      return prom;
    }

    const headers: {[key: string]: string} = {
      'Content-Type': 'application/json',
      // This is a dummy auth header, the trace server requires
      // that we send a basic auth header, but it uses cookies for
      // authentication.
      Authorization: 'Basic ' + btoa(':'),
      'X-Origin': window.location.origin,
    };
    const useAdminPrivileges = getCookie('use_admin_privileges') === 'true';
    if (useAdminPrivileges) {
      headers['use-admin-privileges'] = 'true';
    }

    const fetchOptions: RequestInit = {
      method,
      credentials: 'include',
      headers,
    };

    // Only add body for POST requests
    if (['POST', 'PUT'].includes(method)) {
      fetchOptions.body = reqBody;
    }

    // oxlint-disable-next-line wandb/no-unprefixed-urls
    fetch(url, fetchOptions)
      .then(async response => {
        if (!response.ok) {
          throw new HTTPError(
            response.statusText,
            response.status,
            await response.text()
          );
        }
        if (responseReturnType === 'text') {
          return response.text();
        } else if (responseReturnType === 'arrayBuffer') {
          return response.arrayBuffer();
        } else if (responseReturnType === 'json') {
          return response.json();
        } else {
          // Should never happen with correct type checking
          throw new Error('Invalid responseReturnType: ' + responseReturnType);
        }
      })
      .then(res => {
        try {
          // TODO: fix violation - add nullish coalescing fallback
          const inFlightRequest = [
            // eslint-disable-next-line no-unsafe-optional-chaining
            ...this.inFlightFetchesRequests[endpoint]?.[reqBody],
          ];
          delete this.inFlightFetchesRequests[endpoint][reqBody];
          if (inFlightRequest) {
            inFlightRequest.forEach(({resolve}) => {
              resolve(res);
            });
          }
        } catch (err) {
          console.error(err);
        }
      })
      .catch(err => {
        try {
          // TODO: fix violation - add nullish coalescing fallback
          const inFlightRequest = [
            // eslint-disable-next-line no-unsafe-optional-chaining
            ...this.inFlightFetchesRequests[endpoint]?.[reqBody],
          ];
          delete this.inFlightFetchesRequests[endpoint][reqBody];
          if (inFlightRequest) {
            inFlightRequest.forEach(({reject}) => {
              reject(err);
            });
          }
        } catch (err2) {
          console.error(err2);
        }
      });

    return prom;
  };

  /**
   * Stream request handler that returns the raw Response object.
   * Used when we need to process the response body as a stream.
   * Bypasses the promise cache to ensure each streaming request is independent.
   */
  private makeStreamRequestRaw = async <QT>(
    endpoint: string,
    req: QT,
    contentType?: ContentType,
    signal?: AbortSignal
  ): Promise<Response> => {
    const url = `${this.baseUrl}${endpoint}`;
    const reqBody = JSON.stringify(req);

    const headers: {[key: string]: string} = {
      'Content-Type': 'application/json',
      // This is a dummy auth header, the trace server requires
      // that we send a basic auth header, but it uses cookies for
      // authentication.
      Authorization: 'Basic ' + btoa(':'),
    };
    if (contentType) {
      headers.Accept = contentType;
    }
    const useAdminPrivileges = getCookie('use_admin_privileges') === 'true';
    if (useAdminPrivileges) {
      headers['use-admin-privileges'] = 'true';
    }

    // oxlint-disable-next-line wandb/no-unprefixed-urls
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: reqBody,
      signal,
    });

    if (!response.ok) {
      throw new HTTPError(
        response.statusText,
        response.status,
        await response.text()
      );
    }

    return response;
  };
}

/**
 * Sanitizes an object name by replacing non-alphanumeric characters with dashes and enforcing length limits.
 * This matches the Python implementation in weave_client.py.
 *
 * @param name The name to sanitize
 * @returns The sanitized name
 * @throws Error if the resulting name would be empty
 */
export function sanitizeObjectId(name: string): string {
  // Replace any non-word chars (except dots and underscores) with dashes
  let res = name.replace(/[^\w._]+/g, '-');
  // Replace multiple consecutive dashes/dots/underscores with a single dash
  res = res.replace(/([._-]{2,})+/g, '-');
  // Remove leading/trailing dashes and underscores
  res = res.replace(/^[-_]+|[-_]+$/g, '');

  if (!res) {
    throw new Error(`Invalid object name: ${name}`);
  }

  if (res.length > 128) {
    res = res.slice(0, 128);
  }

  return res;
}

/**
 * Returns whether `name` is a valid object id (i.e. `sanitizeObjectId` would
 * not throw on it).
 */
export function isValidObjectId(name: string): boolean {
  try {
    sanitizeObjectId(name);
    return true;
  } catch {
    return false;
  }
}
